import React from "react";
import { Flex, Text, Icon, Tabs, UseTabsReturn } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { HorizontalTabsSkeleton } from "./HorizontalTabsSkeleton";

export enum HorizontalTabsPlacement {
  EVERYWHERE = "everywhere",
  TALENT_COMMUNITY = "talent-community",
}

export interface TabItem {
  id: string;
  label: string;
  icon: IconType;
  content?: React.ReactNode;
}

export interface HorizontalTabsProps {
  tabs: TabItem[];
  onTabChange?: (tabId: string) => void;
  initialValue?: string;
  showContent?: boolean;
  centerTabs?: boolean;
  fullWidthContent?: boolean;
  isInModal?: boolean;
  isInDrawer?: boolean;
  isEmbeddedContent?: boolean;
  innerModalHeight?: string;
  outerModalHeight?: string;
  innerDrawerHeight?: string;
  disabled?: boolean;
  placementToUse?: HorizontalTabsPlacement;
  tabContext: UseTabsReturn;
  mobileUse?: boolean;
}

export const HorizontalTabs: React.FC<HorizontalTabsProps> = ({
  tabs,
  // initialValue,
  showContent = false,
  centerTabs = false,
  fullWidthContent = false,
  isInModal = false,
  isInDrawer = false,
  isEmbeddedContent = false,
  disabled = false,
  placementToUse: placement = HorizontalTabsPlacement.EVERYWHERE,
  tabContext,
  /**
   * Adjust the height of content when display the inner modal allow to solve double scrollbar issue
   */
  innerModalHeight = "calc(100vh - 16rem)",
  /**
   * Adjust the height of content when display the outer modal allow to solve double scrollbar issue
   */
  outerModalHeight = "calc(100vh - 6rem)",
  innerDrawerHeight = "calc(100vh - 9.5rem)",
  mobileUse = true,
  onTabChange,
}) => {
  const isTalentCommunity =
    placement === HorizontalTabsPlacement.TALENT_COMMUNITY;

  const listBgColor = isTalentCommunity
    ? "themeColor.bg.heading"
    : "themeColor.bg.muted";
  const verticalPadding = isTalentCommunity ? 1 : 3;
  const borderRadius = isTalentCommunity ? "2xl" : "lg";
  const fontWeight = isTalentCommunity ? "bold" : "semibold";

  if (!tabContext) {
    return (
      <HorizontalTabsSkeleton
        tabs={tabs}
        centerTabs={centerTabs}
        showContent={showContent}
        fullWidthContent={fullWidthContent}
        isInModal={isInModal}
        isEmbeddedContent={isEmbeddedContent}
        innerModalHeight={innerModalHeight}
        outerModalHeight={outerModalHeight}
        placementToUse={placement}
      />
    );
  }

  const contentHeight = isInModal
    ? { base: "calc(100vh - 10rem)", md: innerModalHeight }
    : isInDrawer
      ? { base: "calc(100vh - 10rem)", md: innerDrawerHeight }
      : { base: "calc(100vh - 10rem)", md: outerModalHeight };

  const getTabTriggerAppearance = (isSelected: boolean) => ({
    bg: isSelected ? "themeColor.bg.solid" : "transparent",
    color: isSelected ? "themeColor.fg.title" : "themeColor.fg.muted",
    iconColor: isSelected ? "themeColor.fg.active" : "themeColor.fg.inactive",
    labelColor: isSelected ? "themeColor.fg.title" : "themeColor.fg.inactive",
    boxShadow: isSelected ? "themeColor.shadow.default" : "none",
    hover: disabled
      ? {}
      : {
          bg: isSelected ? "themeColor.bg.solid" : "themeColor.sub.hoverBg",
          transform: isSelected ? "none" : "translateY(-2px)",
        },
  });

  return (
    <Tabs.RootProvider
      variant="enclosed"
      value={tabContext}
      size="sm"
      unmountOnExit
      lazyMount
    >
      <Flex justify={centerTabs ? "center" : "flex-start"} width="100%">
        <Tabs.List
          bg={listBgColor}
          p={1.5}
          borderRadius="lg"
          gap={1.5}
          border="none"
          boxShadow="none"
          display="flex"
          w={{ base: "100%", md: "fit-content" }}
          justifyContent={{ base: "space-between", md: "flex-start" }}
          rounded="full"
        >
          {tabs.map((tab) => {
            const isSelected = tabContext?.value === tab.id;
            const isScreeningTab = tab.id === "screening";
            const tabAppearance = getTabTriggerAppearance(isSelected);

            const displayStyles = isScreeningTab
              ? {
                  base: "none",
                  md: mobileUse ? "flex" : "none",
                  lg: "flex",
                }
              : { base: mobileUse ? "flex" : "none", lg: "flex" };

            return (
              <Tabs.Trigger
                key={tab.id}
                value={tab.id}
                bg={tabAppearance.bg}
                color={tabAppearance.color}
                borderRadius={borderRadius}
                cursor={disabled ? "not-allowed" : "pointer"}
                transition="all 0.3s"
                boxShadow={tabAppearance.boxShadow}
                opacity={disabled ? 0.5 : 1}
                _hover={tabAppearance.hover}
                minW={{ base: "4.6rem", md: "fit-content" }}
                border="none"
                outline="none"
                px={{ base: 3, md: 3.5 }}
                py={verticalPadding}
                alignItems="center"
                gap={{ base: 0, md: 2.5 }}
                disabled={disabled}
                display={displayStyles}
                flexDirection={{ base: "column", md: "row" }}
                rounded="full"
                onClick={() => onTabChange?.(tab.id)}
              >
                <Icon
                  as={tab.icon}
                  boxSize={4}
                  color={tabAppearance.iconColor}
                />
                <Text
                  textStyle={{ base: "2xs", md: "sm" }}
                  fontWeight={fontWeight}
                  whiteSpace="nowrap"
                  color={tabAppearance.labelColor}
                >
                  {tab.label}
                </Text>
              </Tabs.Trigger>
            );
          })}
        </Tabs.List>
      </Flex>

      {showContent && (
        <Flex
          mt={2}
          width={fullWidthContent ? "100%" : "auto"}
          border={isEmbeddedContent ? "none" : "1px solid"}
          borderColor={
            isEmbeddedContent ? "transparent" : "themeColor.border.muted"
          }
          rounded="2xl"
          h={contentHeight}
          overflow="auto"
          scrollBehavior="smooth"
          css={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {tabs.map((tab) => (
            <Tabs.Content
              key={tab.id}
              value={tab.id}
              p={isEmbeddedContent ? 0 : 4}
              py={0}
              pr={0}
              width={fullWidthContent ? "100%" : "auto"}
              scrollbarColor="transparent transparent"
            >
              {tab.content}
            </Tabs.Content>
          ))}
        </Flex>
      )}
    </Tabs.RootProvider>
  );
};
